import { jiraTypeLabel } from '@novachat/shared';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { create } from 'zustand';
import { client } from '../lib/client';
import { closeOnBackdrop } from '../lib/dismiss';
import { errorMessage } from '../lib/errors';
import { TypeIcon } from './JiraCards';
import { useJira } from './JiraSettings';
import { useSettings } from './SettingsDialog';

interface Draft {
  /** From a message: its thread says which issue was opened. */
  messageId?: string;
  /** From /jira in a channel: the channel says it. */
  channelId?: string;
  projectKey?: string;
  summary: string;
  description: string;
}

/** "Jira işi aç" (docs/plans/2026-11-04-jira-design.md §4), from a message's toolbar or `/jira`; rendered once by Shell. */
export const useJiraCreate = create<{ draft: Draft | null; open(draft: Draft): void; close(): void }>()((set) => ({
  draft: null,
  open: (draft) => set({ draft }),
  close: () => set({ draft: null }),
}));

const LAST_PROJECT = 'novachat.jira.project';
const SECTION = 'm-0 text-[11.5px] font-semibold tracking-[0.08em] text-fg-3 uppercase';

export function CreateJiraIssueDialog() {
  const draft = useJiraCreate((s) => s.draft);
  return draft ? <Form draft={draft} /> : null;
}

function Toggle({ on, onChange, label }: { on: boolean; onChange: (on: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} className="flex items-center gap-2.5 border-0 bg-transparent p-0 text-[13.5px] text-fg-2">
      <span className={`relative h-5 w-[34px] flex-none rounded-full transition-colors ${on ? 'bg-brand-500' : 'bg-surface-3'}`}>
        <span className={`absolute top-0.5 size-4 rounded-full bg-white transition-[left] ${on ? 'left-4' : 'left-0.5'}`} />
      </span>
      {label}
    </button>
  );
}

/** The new issue, laid out like its details (§7): project and type as chips, a large title, the description. */
function Form({ draft }: { draft: Draft }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const summaryField = useRef<HTMLTextAreaElement>(null);
  const titleId = useId();
  useEffect(() => {
    dialog.current?.showModal();
    // Straight into the title (showModal would focus the close button).
    summaryField.current?.focus();
  }, []);
  const close = () => dialog.current?.close();
  const jira = useJira();
  const projects = useQuery({ queryKey: ['jira-projects'], queryFn: () => client.api.jiraProjects(), enabled: !!jira.data?.connection, staleTime: 5 * 60_000 });
  const [projectKey, setProjectKey] = useState(() => {
    if (draft.projectKey) return draft.projectKey;
    try {
      return localStorage.getItem(LAST_PROJECT) ?? '';
    } catch {
      return '';
    }
  });
  const project = projects.data?.find((p) => p.key === projectKey) ?? null;
  const [issueType, setIssueType] = useState('');
  const [summary, setSummary] = useState(draft.summary.slice(0, 255));
  const [description, setDescription] = useState(draft.description);
  const [assignToMe, setAssignToMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    // The last project is remembered; otherwise the first is picked for you.
    if (!project && projects.data?.length) setProjectKey(projects.data[0]!.key);
  }, [project, projects.data]);
  useEffect(() => {
    if (project && !project.issueTypes.includes(issueType)) setIssueType(project.issueTypes[0] ?? '');
  }, [project, issueType]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!project) return setError('Bir proje seç.');
    if (!summary.trim()) return setError('Başlık boş olamaz.');
    setBusy(true);
    setError(null);
    try {
      await client.api.createJiraIssue({
        projectKey: project.key,
        issueType,
        summary: summary.trim(),
        description,
        assignToMe,
        ...(draft.messageId ? { messageId: draft.messageId } : {}),
        ...(draft.channelId ? { channelId: draft.channelId } : {}),
      });
      try {
        localStorage.setItem(LAST_PROJECT, project.key);
      } catch {
        // a remembered project is only a convenience
      }
      close();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  const pill = (on: boolean) =>
    `nc-press flex h-[34px] items-center gap-2 rounded-full border px-3.5 text-[13.5px] ${on ? 'border-brand-500/70 bg-brand-500/15 text-fg-1' : 'border-white/[0.08] bg-transparent text-fg-2 hover:text-fg-1'}`;
  const chip = (on: boolean) =>
    `nc-press flex h-[34px] items-center gap-2 rounded-[10px] border px-3 text-[13.5px] font-medium ${on ? 'border-white/25 bg-white/[0.09] text-fg-1' : 'border-white/[0.08] bg-transparent text-fg-2 hover:text-fg-1'}`;

  return (
    <dialog
      ref={dialog}
      onClick={closeOnBackdrop}
      onClose={() => useJiraCreate.getState().close()}
      aria-labelledby={titleId}
      className="m-auto h-dvh max-h-none w-full max-w-none overflow-hidden border-0 bg-surface-2 p-0 text-fg-1 backdrop:bg-[rgba(5,6,8,0.62)] md:h-fit md:max-h-[calc(100dvh-96px)] md:w-[min(640px,calc(100vw-48px))] md:rounded-2xl md:border md:border-white/[0.08] md:shadow-[0_30px_80px_rgba(0,0,0,0.55)]"
    >
      <form onSubmit={(e) => void submit(e)} noValidate className="flex h-full max-h-[inherit] flex-col md:h-auto">
        <header className="flex flex-none items-start gap-3 border-b border-white/[0.07] px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4 md:px-6">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="m-0 text-[18px] font-semibold tracking-[-0.01em]">
              Yeni Jira işi
            </h2>
            <p className="m-0 mt-1 text-[13px] text-fg-3">
              {draft.messageId ? 'Açılınca mesajın dalına bağlantısı yazılır.' : draft.channelId ? 'Açılınca kanala bağlantısı yazılır.' : 'Jira’da senin adınla açılır.'}
            </p>
          </div>
          <button type="button" aria-label="Kapat" onClick={close} className="nc-press -mt-1 -mr-2 grid size-8 flex-none place-items-center rounded-md border-0 bg-transparent text-fg-2 hover:bg-white/[0.06]">
            <X size={16} />
          </button>
        </header>

        {!jira.data?.connection ? (
          <div className="p-6">
            <p className="m-0 text-[14px] text-fg-2">İş açmak için önce Jira hesabını bağla.</p>
            <button
              type="button"
              onClick={() => {
                close();
                useSettings.getState().show('connections');
              }}
              className="nc-press mt-3 h-9 rounded-md border-0 bg-brand-500 px-3.5 text-[13.5px] font-bold text-white"
            >
              Jira hesabını bağla
            </button>
          </div>
        ) : (
          <>
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5 md:flex-initial md:px-6">
              <div className="flex flex-col gap-2.5">
                <p className={SECTION}>Proje</p>
                <div role="radiogroup" aria-label="Proje" className="flex flex-wrap gap-1.5">
                  {projects.isPending && <span className="text-[13px] text-fg-3">Projeler yükleniyor…</span>}
                  {projects.data?.map((p) => (
                    <button key={p.key} type="button" role="radio" aria-checked={p.key === projectKey} onClick={() => setProjectKey(p.key)} className={pill(p.key === projectKey)}>
                      <span className="font-semibold">{p.key}</span>
                      <span className="text-fg-3">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              {project && (
                <div className="flex flex-col gap-2.5">
                  <p className={SECTION}>Tür</p>
                  <div role="radiogroup" aria-label="Tür" className="flex flex-wrap gap-1.5">
                    {project.issueTypes.map((t) => (
                      <button key={t} type="button" role="radio" aria-checked={t === issueType} onClick={() => setIssueType(t)} className={chip(t === issueType)}>
                        <span aria-hidden="true" className="-mt-0.5">
                          <TypeIcon type={t} />
                        </span>
                        {jiraTypeLabel(t)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2.5">
                <label htmlFor={`${titleId}-summary`} className={SECTION}>
                  Başlık
                </label>
                {/* A title wraps instead of scrolling sideways; Enter goes on to the description. */}
                <textarea
                  id={`${titleId}-summary`}
                  ref={summaryField}
                  rows={1}
                  value={summary}
                  maxLength={255}
                  onChange={(e) => setSummary(e.target.value.replace(/\s*\n\s*/g, ' '))}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    document.getElementById(`${titleId}-description`)?.focus();
                  }}
                  placeholder="Admin FE: Ne yapılacak (NOV-…)"
                  className="min-h-12 resize-none rounded-xl border border-white/[0.08] bg-surface-1 px-3.5 py-[11px] text-[17px] leading-snug font-semibold text-fg-1 outline-none [field-sizing:content] placeholder:font-normal placeholder:text-fg-3 focus:border-brand-400"
                />
              </div>
              <div className="flex flex-col gap-2.5">
                <label htmlFor={`${titleId}-description`} className={SECTION}>
                  Açıklama
                </label>
                <textarea
                  id={`${titleId}-description`}
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="İsteğe bağlı"
                  className="min-h-[120px] resize-y rounded-xl border border-white/[0.08] bg-surface-1 px-3.5 py-3 text-base leading-relaxed text-fg-1 outline-none placeholder:text-fg-3 focus:border-brand-400 sm:text-[14.5px]"
                />
              </div>
              {error && (
                <p role="alert" className="m-0 text-[13.5px] text-[#EE9191]">
                  {error}
                </p>
              )}
            </div>
            <footer className="flex flex-none flex-wrap items-center gap-3 border-t border-white/[0.07] px-5 pt-3.5 pb-[max(14px,env(safe-area-inset-bottom))] md:px-6">
              <Toggle on={assignToMe} onChange={setAssignToMe} label="Bana ata" />
              <span className="flex-1" />
              <button type="button" onClick={close} className="nc-press h-10 rounded-[10px] border border-white/10 bg-transparent px-4 text-[13.5px] font-semibold text-fg-1 hover:bg-white/[0.05]">
                Vazgeç
              </button>
              <button type="submit" disabled={busy} className="nc-press h-10 rounded-[10px] border-0 bg-brand-500 px-5 text-[13.5px] font-bold text-[#1A1208] disabled:opacity-60">
                {busy ? 'Açılıyor…' : 'İşi aç'}
              </button>
            </footer>
          </>
        )}
      </form>
    </dialog>
  );
}
